import React, { useState, useEffect } from 'react';
import './DesktopWidgets.css';

const DesktopWidgets = () => {
  // Clock State
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // IP Address State
  const [ipAddress, setIpAddress] = useState('Fetching...');
  const [ipLoading, setIpLoading] = useState(true);

  // Security Wisdom State
  const [currentWisdom, setCurrentWisdom] = useState(null);

  // Hover State for reveal animation
  const [isHovered, setIsHovered] = useState(false);

  // YouTube Video Carousel State
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  // Threat Intelligence Feed
  const threatIntel = [
    {
      type: 'critical',
      severity: 'critical',
      text: 'Zero-day exploits in popular web frameworks detected. Patch immediately!',
      icon: '⚠️'
    },
    {
      type: 'warning',
      severity: 'warning',
      text: 'SQL injection remains in the OWASP Top 10. Always use parameterized queries!',
      icon: '⚠️'
    },
    {
      type: 'critical',
      severity: 'critical',
      text: 'Ransomware attacks increased by 300%. Backup everything, trust nothing.',
      icon: '⚠️'
    },
    {
      type: 'warning',
      severity: 'warning',
      text: 'Enable 2FA everywhere. Your password alone is not enough protection.',
      icon: '⚠️'
    },
    {
      type: 'critical',
      severity: 'critical',
      text: 'Supply chain attacks targeting npm packages. Audit your dependencies!',
      icon: '⚠️'
    },
    {
      type: 'warning',
      severity: 'warning',
      text: 'Never trust user  input. Validate, sanitize, and escape everything.',
      icon: '⚠️'
    },
    {
      type: 'critical',
      severity: 'critical',
      text: 'API keys exposed in GitHub repos lead to 80% of cloud breaches.',
      icon: '⚠️'
    },
    {
      type: 'warning',
      severity: 'warning',
      text: 'Keep your software updated. Yesterday\'s patch is today\'s vulnerability.',
      icon: '⚠️'
    },
    {
      type: 'warning',
      severity: 'warning',
      text: 'Use unique passwords for every account. A password manager is your best friend.',
      icon: '⚠️'
    },
    {
      type: 'critical',
      severity: 'critical',
      text: 'Phishing attacks using AI-generated content are bypassing traditional filters.',
      icon: '⚠️'
    }
  ];

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Select random threat intel on mount and every 15 seconds
  useEffect(() => {
    const selectRandomThreat = () => {
      const randomIndex = Math.floor(Math.random() * threatIntel.length);
      setCurrentWisdom(threatIntel[randomIndex]);
    };

    selectRandomThreat();
    const threatInterval = setInterval(selectRandomThreat, 15000); // Change every 15 seconds

    return () => clearInterval(threatInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-rotate YouTube videos every 4 seconds
  useEffect(() => {
    const youtubeVideos = [
      {
        videoUrl: 'https://youtu.be/tnKJZ1VsdqI',
        thumbnailUrl: 'https://img.youtube.com/vi/tnKJZ1VsdqI/hqdefault.jpg',
        videoTitle: 'Nmap Explained | The ONLY Guide You Need in 2026'
      },
      {
        videoUrl: 'https://youtu.be/RW35vCuk3Vs',
        thumbnailUrl: 'https://img.youtube.com/vi/RW35vCuk3Vs/hqdefault.jpg',
        videoTitle: 'Permission Scanner - Linux Security Tool Demo'
      },
      {
        videoUrl: 'https://youtu.be/tcoThJlO56A',
        thumbnailUrl: 'https://img.youtube.com/vi/tcoThJlO56A/hqdefault.jpg',
        videoTitle: 'WPA Key Capturing and Cracking|| Wi-Fi Hacking|| Wireshark || Aircrack-ng ||'
      },
      {
        videoUrl: 'https://youtu.be/ympDYwlDWxg',
        thumbnailUrl: 'https://img.youtube.com/vi/ympDYwlDWxg/hqdefault.jpg',
        videoTitle: 'I Passed the eJPT! (My Full Guide, Tips, & CEH Comparison)'
      }
      // Add more videos here as needed
    ];

    const videoRotationInterval = setInterval(() => {
      setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % youtubeVideos.length);
    }, 4000); // Change video every 4 seconds

    return () => clearInterval(videoRotationInterval);
  }, []);

  // Fetch IP Address
  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then(response => response.json())
      .then(data => {
        setIpAddress(data.ip);
        setIpLoading(false);
      })
      .catch(error => {
        console.error('Error fetching IP:', error);
        setIpAddress('Unavailable');
        setIpLoading(false);
      });
  }, []);

  // Format time
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Format date
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric'
    });
  };

  // Calendar event markers - Add your upcoming events here
  const eventDates = [
    { day: 20, type: 'ctf', label: 'TryHackMe CTF' },
    { day: 25, type: 'exam', label: 'Security+ Exam' },
    { day: 28, type: 'ctf', label: 'HackTheBox Event' }
  ];

  // Get calendar data with events
  const getCalendarDays = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = date.getDate();

    const days = [];
    // Add empty slots for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    // Add actual days with event information
    for (let day = 1; day <= daysInMonth; day++) {
      const event = eventDates.find(e => e.day === day);
      days.push({ day, event });
    }
    return { days, today };
  };

  const calendarData = getCalendarDays();

  return (
    <>
      <div 
        className={`desktop-widgets ${isHovered ? 'widgets-visible' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
      
      {/* Clock Widget - Enhanced with UTC & Uptime */}
      <div className="widget clock-widget">
        <div className="widget-header">
          <span className="widget-icon">🕐</span>
          <span className="widget-title">System Time</span>
        </div>
        <div className="widget-content">
          <div className="time-row">
            <div className="time-label">LOCAL</div>
            <div className="clock-time">{formatTime(currentTime)}</div>
          </div>
          <div className="time-row">
            <div className="time-label">UTC</div>
            <div className="clock-time-utc">{currentTime.toUTCString().slice(17, 25)}</div>
          </div>
          <div className="clock-date">{formatDate(currentTime)}</div>
          <div className="uptime-section">
            <div className="uptime-label">UPTIME</div>
            <div className="uptime-value">20 Years</div>
          </div>
        </div>
      </div>

      {/* Calendar Widget with Events */}
      <div className="widget calendar-widget">
        <div className="widget-header">
          <span className="widget-icon">📅</span>
          <span className="widget-title">{currentTime.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
        </div>
        <div className="widget-content">
          <div className="calendar-grid">
            <div className="calendar-weekdays">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                <div key={i} className="weekday">{day}</div>
              ))}
            </div>
            <div className="calendar-days">
              {calendarData.days.map((dayData, i) => {
                const day = dayData?.day;
                const event = dayData?.event;
                return (
                  <div 
                    key={i} 
                    className={`calendar-day ${day === calendarData.today ? 'today' : ''} ${day === null ? 'empty' : ''}`}
                    title={event ? event.label : ''}
                  >
                    {day}
                    {event && (
                      <div className={`event-marker ${event.type}`}></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="calendar-legend">
            <div className="legend-item">
              <div className="legend-dot ctf"></div>
              <span>CTF Competition</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot exam"></div>
              <span>Exam Date</span>
            </div>
          </div>
        </div>
      </div>

      {/* YouTube Video Carousel Widget - Auto-rotating */}
      <div className="widget youtube-widget">
        <div className="widget-content">
          {(() => {
            // Video array for carousel
            const youtubeVideos = [
              {
                videoUrl: 'https://youtu.be/tnKJZ1VsdqI',
                thumbnailUrl: 'https://img.youtube.com/vi/tnKJZ1VsdqI/hqdefault.jpg',
                videoTitle: 'Nmap Explained | The ONLY Guide You Need in 2026'
              },
              {
                videoUrl: 'https://youtu.be/RW35vCuk3Vs',
                thumbnailUrl: 'https://img.youtube.com/vi/RW35vCuk3Vs/hqdefault.jpg',
                videoTitle: 'Permission Scanner - Linux Security Tool Demo'
              },
              {
                videoUrl: 'https://youtu.be/tcoThJlO56A',
                thumbnailUrl: 'https://img.youtube.com/vi/tcoThJlO56A/hqdefault.jpg',
                videoTitle: 'WPA Key Capturing and Cracking|| Wi-Fi Hacking|| Wireshark || Aircrack-ng ||'
              },
              {
                videoUrl: 'https://youtu.be/ympDYwlDWxg',
                thumbnailUrl: 'https://img.youtube.com/vi/ympDYwlDWxg/hqdefault.jpg',
                videoTitle: 'I Passed the eJPT! (My Full Guide, Tips, & CEH Comparison)'
              }
            ];

            const currentVideo = youtubeVideos[currentVideoIndex];

            return (
              <>
                <a 
                  href={currentVideo.videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="youtube-thumbnail-link"
                >
                  <div className="live-feed-badge">LIVE FEED</div>
                  <div className="youtube-thumbnail">
                    <img 
                      key={currentVideoIndex}
                      src={currentVideo.thumbnailUrl} 
                      alt="YouTube Video"
                      className="thumbnail-image"
                    />
                    <div className="play-overlay">
                      <div className="play-button">▶</div>
                    </div>
                  </div>
                </a>
                <div className="youtube-info">
                  <div className="video-title" key={currentVideoIndex}>{currentVideo.videoTitle}</div>
                </div>
                {/* Carousel indicators */}
                <div className="video-carousel-indicators">
                  {youtubeVideos.map((_, index) => (
                    <div 
                      key={index}
                      className={`carousel-dot ${index === currentVideoIndex ? 'active' : ''}`}
                      onClick={() => setCurrentVideoIndex(index)}
                    />
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* Threat Intelligence Widget */}
      <div className={`widget threat-intel-widget ${currentWisdom?.severity || 'warning'}`}>
        <div className="widget-header">
          <span className="widget-icon">⚠️</span>
          <span className="widget-title">THREAT INTEL</span>
        </div>
        <div className="widget-content">
          {currentWisdom && (
            <div className="threat-alert">
              <div className="threat-severity">{currentWisdom.severity?.toUpperCase() || 'WARNING'}</div>
              <div className="threat-text">{currentWisdom.text}</div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Notes Widget */}
      <div className="widget notes-widget">
        <div className="widget-header">
          <span className="widget-icon">�</span>
          <span className="widget-title">Quick Notes</span>
        </div>
        <div className="widget-content">
          <div className="notes-list">
            <div className="note-item">
              <span className="note-bullet">•</span>
              <span className="note-text">Explore portfolio projects</span>
            </div>
            <div className="note-item">
              <span className="note-bullet">•</span>
              <span className="note-text">Check out CTF challenges</span>
            </div>
            <div className="note-item">
              <span className="note-bullet">•</span>
              <span className="note-text">Download resume</span>
            </div>
          </div>
        </div>
      </div>

      {/* IP Address Widget */}
      <div className="widget ip-widget">
        <div className="widget-header">
          <span className="widget-icon">🌐</span>
          <span className="widget-title">Your IP</span>
        </div>
        <div className="widget-content">
          <div className="ip-display">
            {ipLoading ? (
              <div className="ip-loading">
                <span className="loading-dots">Fetching</span>
              </div>
            ) : (
              <div className="ip-address">{ipAddress}</div>
            )}
          </div>
        </div>
      </div>


    </div>

    {/* Hover trigger area - invisible area on right edge */}
    <div 
      className="widget-trigger-area"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    />
    </>
  );
};

export default DesktopWidgets;
